import styles from "./IntegrationsQuickbooksSyncActivities.module.scss";
import {
  useIsMobile,
  WSButton,
  WSCheckboxGroup,
  WSCheckboxToggle,
  WSCircleIconProps,
  WSDateInput,
  WSFilters,
  WSFlexBox,
  WSForm,
  WSInfiniteScroll,
  WSPage,
  WSRadioInputGroup,
  WSTable,
  WSTableItem,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import {
  INTEGRATIONS_QUICKBOOKS_SYNC_ACTIVITIES_QUERY_FILTERS_DEFAULT,
  IntegrationsQuickbooksSyncActivitiesQueryFilters,
  useIntegrationsQuickbooksSyncActivities
} from "../../../../query/integrations/queries";
import { InvoicesDateRangeFilter } from "../../../../query/payments/queries";
import { WSQueries } from "../../../../query/WSQuery";
import { useUrlQueryFilters } from "../../../../utils/router";
import {
  IIntegrationsQuicbooksSyncActivityAction,
  IIntegrationsQuickbooksSyncActivityGetResponse
} from "@wingspanhq/integrations/dist/lib/interfaces/quickbooks-api/integrationsQuickbooksSyncActivity";
import { openInNewTab } from "../../../../utils/openInNewTab";
import { useHistory } from "react-router-dom";

type SyncActivitiesTableItem = WSTableItem<SyncActivitiesTableItemData>;

type SyncActivitiesTableItemData = {
  syncActivity: IIntegrationsQuickbooksSyncActivityGetResponse;
};

export const IntegrationsQuickbooksSyncActivities: React.FC = () => {
  const isMobile = useIsMobile();
  const history = useHistory();

  const defaultFilters: IntegrationsQuickbooksSyncActivitiesQueryFilters = INTEGRATIONS_QUICKBOOKS_SYNC_ACTIVITIES_QUERY_FILTERS_DEFAULT;

  const { filters, setFilters } = useUrlQueryFilters<
    IntegrationsQuickbooksSyncActivitiesQueryFilters
  >(defaultFilters);

  const qIntegrationsQuickbooksSyncActivities = useIntegrationsQuickbooksSyncActivities(
    filters,
    {
      size: 20
    }
  );

  return (
    <WSPage
      title="Quickbooks Sync History"
      breadcrumb={{
        label: "Integrations",
        icon: "back",
        onClick: () => {
          history.push("/member/settings/integrations");
        }
      }}
    >
      <WSQueries queries={{ qIntegrationsQuickbooksSyncActivities }}>
        {({
          qIntegrationsQuickbooksSyncActivities: {
            data: { data: syncActivities }
          }
        }) => {
          let data: SyncActivitiesTableItem[] = [
            ...syncActivities.map(syncActivity => {
              const icon: WSCircleIconProps = syncActivity.isError
                ? {
                    icon: "warning",
                    color: "red100",
                    secondaryColor: "red500"
                  }
                : {
                    icon: "successMark",
                    color: "green100",
                    secondaryColor: "green500"
                  };
              return {
                id: syncActivity.entityId,
                data: {
                  syncActivity: {
                    ...syncActivity,
                    displayName: syncActivity.displayName || "N/A"
                  }
                },
                icon
              };
            })
          ];

          return (
            <>
              <WSFilters
                my="M"
                mb="M"
                filters={filters}
                defaultFilters={defaultFilters}
                onFilter={newFilters => {
                  const updatedFilters = {
                    ...filters,
                    ...newFilters
                  };
                  setFilters(updatedFilters);
                  qIntegrationsQuickbooksSyncActivities.refetch();
                }}
              >
                <WSForm.Field
                  name="createdAtRange"
                  label="Sync date range"
                  mb="L"
                  component={WSRadioInputGroup}
                  componentProps={{
                    options: [
                      {
                        label: "All time",
                        value: InvoicesDateRangeFilter.All
                      },
                      {
                        label: "Past week",
                        value: InvoicesDateRangeFilter.PastWeek
                      },
                      {
                        label: "Past month",
                        value: InvoicesDateRangeFilter.PastMonth
                      },
                      {
                        label: "Past year",
                        value: InvoicesDateRangeFilter.PastYear
                      },
                      {
                        label: (
                          <WSForm.Context>
                            {({ setValue }) => (
                              <WSFlexBox
                                alignItems="center"
                                className={styles.dateRange}
                              >
                                <WSText mr="S">From</WSText>
                                <WSForm.Field
                                  name="customCreatedAtRange"
                                  component={WSDateInput}
                                  componentProps={{
                                    range: true,
                                    placeholder: "Select date range",
                                    onFocus() {
                                      setValue(
                                        "createdAtRange",
                                        InvoicesDateRangeFilter.Custom
                                      );
                                    }
                                  }}
                                />
                              </WSFlexBox>
                            )}
                          </WSForm.Context>
                        ),
                        value: InvoicesDateRangeFilter.Custom
                      }
                    ]
                  }}
                />
                <WSForm.Field
                  name={`onlyErrors`}
                  component={WSCheckboxToggle}
                  componentProps={{ label: "Only errors" }}
                  mb="L"
                />
                <WSForm.Field
                  name="actions"
                  label="Action"
                  mb="L"
                  component={WSCheckboxGroup}
                  componentProps={{
                    options: Object.keys(
                      IIntegrationsQuicbooksSyncActivityAction
                    ).map(key => ({
                      label: key,
                      value: key
                    }))
                  }}
                />
              </WSFilters>
              <WSInfiniteScroll
                onLoad={() => {
                  qIntegrationsQuickbooksSyncActivities.fetchMore();
                }}
                loadMore={syncActivities.length > 0}
                endOfList={!qIntegrationsQuickbooksSyncActivities.canFetchMore}
                loading={!!qIntegrationsQuickbooksSyncActivities.isFetchingMore}
              >
                <WSTable<SyncActivitiesTableItemData>
                  showIcon
                  tableData={data}
                  columns={[
                    {
                      config: {
                        justify: "start",
                        header: {
                          text: "Object"
                        },
                        hideOnScreens: ["XS", "S"]
                      },
                      renderFunction: ({ data: { syncActivity } }) => (
                        <WSText>{syncActivity.qboEntityName}</WSText>
                      )
                    },
                    {
                      config: {
                        justify: "start",
                        header: {
                          text: "Name"
                        },
                        hideOnScreens: ["XS", "S"]
                      },
                      renderFunction: ({ data: { syncActivity } }) => (
                        <WSText>{syncActivity.displayName}</WSText>
                      )
                    },
                    {
                      config: {
                        justify: "start",
                        header: {
                          text: "Action"
                        },
                        hideOnScreens: ["XS", "S"]
                      },
                      renderFunction: ({ data: { syncActivity } }) => (
                        <WSText>{syncActivity.action}</WSText>
                      )
                    },
                    {
                      config: {
                        gridTemplateSizeMax: "1fr",
                        header: {
                          text: "Details"
                        }
                      },
                      renderFunction: ({ data: { syncActivity } }) => {
                        return (
                          <>
                            <WSText>
                              {`${
                                syncActivity.isError
                                  ? `Failed to `
                                  : `Successfully `
                              }${syncActivity.action.toLocaleLowerCase()}${
                                syncActivity.isError ? `` : `d`
                              } ${syncActivity.qboEntityName} `}
                              <WSButton.Link
                                inline
                                onClick={() => {
                                  openInNewTab(
                                    syncActivity.qboEntityName === "Invoice"
                                      ? `/member/invoices/${syncActivity.entityId}`
                                      : syncActivity.qboEntityName === "Bill"
                                      ? `/member/invoices/payables/dashboard/${syncActivity.entityId}`
                                      : syncActivity.qboEntityName ===
                                        "Customer"
                                      ? `/member/invoices/contacts/clients/${syncActivity.entityId}`
                                      : syncActivity.qboEntityName === "Vendor"
                                      ? `/member/invoices/contacts/collaborators/${syncActivity.entityId}`
                                      : ``
                                  );
                                }}
                              >
                                {syncActivity.displayName}
                              </WSButton.Link>
                              {` ${
                                syncActivity.action ===
                                IIntegrationsQuicbooksSyncActivityAction.Delete
                                  ? `from`
                                  : `in`
                              } QBO${
                                syncActivity.message === "MarkedAsPaid"
                                  ? ", marked as paid."
                                  : ""
                              }`}
                            </WSText>
                            {isMobile && (
                              <WSText.LabelExtraSmall
                                mb="S"
                                color="grey500"
                                formatDate
                              >
                                {syncActivity.createdAt}
                              </WSText.LabelExtraSmall>
                            )}
                          </>
                        );
                      }
                    },
                    {
                      config: {
                        justify: "end",
                        header: {
                          text: "Synced Date"
                        },
                        hideOnScreens: ["XS", "S"]
                      },
                      renderFunction: ({ data: { syncActivity } }) => (
                        <WSText formatDate>{syncActivity.createdAt}</WSText>
                      )
                    }
                  ]}
                  data-testid="syncActivitiesTable"
                  mb="L"
                />
              </WSInfiniteScroll>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
